// Shared replenishment-rule maths, used by the rule editor (simulation,
// draft diff, derived review cycle) and the server (audit rows, future run
// engine). Keep pure — no React, no DB.

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const
export type Weekday = (typeof WEEKDAYS)[number]

export type RuleParams = {
  thresholdMode: "days" | "units"
  rosWindowDays: number
  minEligibleDays: number
  cycleMode: "fixed" | "schedule"
  reviewCycleDays: number
  deliveryDays: string[]
  repeatIntervalWeeks: number
  cutoffTime: string
  safetyCoverDays: number
  overstockFactor: number
  unitMin: number
  unitOpt: number
  unitOver: number
}

export const RULE_DEFAULTS: RuleParams = {
  thresholdMode: "days",
  rosWindowDays: 28,
  minEligibleDays: 10,
  cycleMode: "schedule",
  reviewCycleDays: 7,
  deliveryDays: ["Mon"],
  repeatIntervalWeeks: 1,
  cutoffTime: "12:00",
  safetyCoverDays: 4,
  overstockFactor: 1.5,
  unitMin: 0,
  unitOpt: 0,
  unitOver: 0,
}

/** Average gap in days between selected delivery weekdays, wrapping over a
 * repeatIntervalWeeks-week span (Mon/Wed/Fri weekly -> 2.3). Null when no
 * days are selected. */
export function deriveReviewCycleDays(
  deliveryDays: string[],
  repeatIntervalWeeks: number,
): number | null {
  const gaps = departureGaps(deliveryDays, repeatIntervalWeeks)
  if (gaps.length === 0) return null
  return Math.round((gaps.reduce((a, b) => a + b, 0) / gaps.length) * 10) / 10
}

/** The days from each selected weekday to the next one, wrapping over the repeat span. */
function departureGaps(deliveryDays: string[], repeatIntervalWeeks: number): number[] {
  const indexes = WEEKDAYS.map((day, i) => (deliveryDays.includes(day) ? i : -1)).filter(
    (i) => i >= 0,
  )
  const span = 7 * Math.max(1, repeatIntervalWeeks)
  return indexes.map((value, i) =>
    i === indexes.length - 1 ? (indexes[0] ?? 0) + span - value : (indexes[i + 1] ?? 0) - value,
  )
}

/** "3.5d" for a fixed review period or evenly spaced departures, "1 to 6d" when the gaps differ. */
export function reviewCycleText(rule: RuleParams): string {
  const gaps =
    rule.cycleMode === "schedule" ? departureGaps(rule.deliveryDays, rule.repeatIntervalWeeks) : []
  const min = Math.min(...gaps)
  const max = Math.max(...gaps)
  return gaps.length > 0 && min !== max
    ? `${min} to ${max}d`
    : `${dec1(effectiveReviewCycleDays(rule))}d`
}

/** The review cycle the maths runs on: derived from the schedule when in
 * schedule mode (falling back to the stored value when no days are picked). */
export function effectiveReviewCycleDays(rule: RuleParams): number {
  if (rule.cycleMode === "schedule") {
    return (
      deriveReviewCycleDays(rule.deliveryDays, rule.repeatIntervalWeeks) ?? rule.reviewCycleDays
    )
  }
  return rule.reviewCycleDays
}

export type StockLevels = {
  min: number
  optimal: number
  overstock: number
  proposed: number
  coverToMinDays: number
  coverToOptDays: number
}

/** min/optimal/overstock and the proposed top-up for one SKU in one store.
 * "days" mode derives levels from ROS x cover windows; "units" mode pins them
 * to the rule's fixed unit targets. A line's minimum display quantity never
 * moves a level. Proposed = max(optimal - position, 0); the overstock level
 * only warns, it never trims the proposal. */
export function computeStockLevels(
  rule: RuleParams,
  input: {
    ros: number
    leadTimeDays: number
    position: number
    /** The gap after the run's departure; the rule's average review period when absent. */
    reviewDays?: number
  },
): StockLevels {
  const review = input.reviewDays ?? effectiveReviewCycleDays(rule)
  const coverToMinDays = input.leadTimeDays + rule.safetyCoverDays
  const coverToOptDays = input.leadTimeDays + review + rule.safetyCoverDays

  let min: number
  let optimal: number
  let overstock: number
  if (rule.thresholdMode === "units") {
    min = rule.unitMin
    optimal = Math.max(rule.unitOpt, min)
    overstock = Math.max(rule.unitOver, optimal)
  } else {
    min = Math.ceil(input.ros * coverToMinDays)
    optimal = Math.ceil(input.ros * coverToOptDays)
    overstock = Math.ceil(input.ros * coverToOptDays * rule.overstockFactor)
  }

  const proposed = Math.max(0, optimal - input.position)

  return { min, optimal, overstock, proposed, coverToMinDays, coverToOptDays }
}

export type RuleFieldChange = { label: string; field: string; before: string; after: string }

const dec1 = (n: number) => Number(n).toFixed(1)

/** Field-level diff between two parameter sets, in audit-log shape (machine
 * field codes, human labels, rendered before/after). */
export function diffRuleParams(before: RuleParams, after: RuleParams): RuleFieldChange[] {
  const out: RuleFieldChange[] = []
  // Labels and machine codes come from docs/ALLOCATION_PLANNING_TERMINOLOGY.md.
  const scalar: [keyof RuleParams, string, string][] = [
    ["rosWindowDays", "Sales history window", "window_days"],
    ["minEligibleDays", "Minimum days with inventory", "min_days_with_inventory"],
    ["safetyCoverDays", "Safety stock (days)", "safety_cover_days"],
    ["overstockFactor", "Overstock factor", "overstock_factor"],
    ["cycleMode", "Review period source", "cycle_mode"],
  ]
  for (const [key, label, code] of scalar) {
    if (String(before[key]) !== String(after[key])) {
      out.push({ label, field: code, before: String(before[key]), after: String(after[key]) })
    }
  }
  if (after.cycleMode === "schedule" && before.repeatIntervalWeeks !== after.repeatIntervalWeeks) {
    out.push({
      label: "Repeat every",
      field: "repeat_interval_weeks",
      before: `${before.repeatIntervalWeeks} wk`,
      after: `${after.repeatIntervalWeeks} wk`,
    })
  }
  if (
    after.cycleMode === "schedule" &&
    before.deliveryDays.join(",") !== after.deliveryDays.join(",")
  ) {
    out.push({
      label: "Departure calendar",
      field: "delivery_days",
      before: before.deliveryDays.join("/") || "none",
      after: after.deliveryDays.join("/") || "none",
    })
  }
  const beforeReview = reviewCycleText(before)
  const afterReview = reviewCycleText(after)
  if (beforeReview !== afterReview) {
    out.push({
      label: "Review period (days)",
      field: "review_period_days",
      before: beforeReview,
      after: afterReview,
    })
  }
  if (before.cutoffTime !== after.cutoffTime) {
    out.push({
      label: "Cut-off time (UTC)",
      field: "cutoff_time",
      before: before.cutoffTime,
      after: after.cutoffTime,
    })
  }
  if (before.thresholdMode !== after.thresholdMode) {
    const render = (mode: string) => (mode === "days" ? "days of stock" : "unit quantity")
    out.push({
      label: "Targets based on",
      field: "threshold_mode",
      before: render(before.thresholdMode),
      after: render(after.thresholdMode),
    })
  }
  if (after.thresholdMode === "units") {
    const units: [keyof RuleParams, string, string][] = [
      ["unitMin", "Reorder point", "unit_min"],
      ["unitOpt", "Order-up-to level", "unit_opt"],
      ["unitOver", "Overstock level", "unit_over"],
    ]
    for (const [key, label, code] of units) {
      if (before[key] !== after[key]) {
        out.push({
          label,
          field: code,
          before: `${String(before[key])} u`,
          after: `${String(after[key])} u`,
        })
      }
    }
  }
  return out
}
